import { API } from "aws-amplify";
import type { ModuleBuilderCatalogResponse } from "./catalogTypes";

const CATALOG_PATH = "admin/module-builder/catalog";

export type FetchModuleBuilderCatalogParams = {
  instrumentTitleContains?: string;
  instrumentLimit?: number;
};

export async function fetchModuleBuilderCatalog(
  params?: FetchModuleBuilderCatalogParams
): Promise<ModuleBuilderCatalogResponse> {
  return (await API.post("simpl-api", CATALOG_PATH, {
    body: params ?? {}
  })) as ModuleBuilderCatalogResponse;
}

// ---------- Reuse-driven endpoints (existing simpl-api routes) ----------
//
// These read endpoints aren't SysAdmin-gated themselves, but the Module
// Builder UI is. They feed the typeahead + reuse picker flows.

// (Network listing now reads from Redux via useAllNetworks — see NetworkSelector.)

type InstrumentSearchResponse = {
  instruments: Array<{
    id: string;
    title: string;
    description: string | null;
  }>;
  total?: number;
};

export async function searchInstrumentsByTitle(
  titleContains: string
): Promise<InstrumentSearchResponse> {
  return (await API.get("simpl-api", "instruments/search", {
    queryStringParameters: {
      filter: JSON.stringify({ title: { contains: titleContains } }),
      summary: "true"
    }
  })) as InstrumentSearchResponse;
}

// Loads every instrument (id/title/description only) — used to populate the
// reuse typeahead so the user sees all candidates the moment a network is
// picked, without having to type. Backed by the `summary=true` mode of the
// search-instruments Lambda which skips per-row question/author enrichment.
export async function listAllInstrumentSummaries(): Promise<InstrumentSearchResponse> {
  return (await API.get("simpl-api", "instruments/search", {
    queryStringParameters: { summary: "true" }
  })) as InstrumentSearchResponse;
}

type GetInstrumentResponse = {
  instrument: {
    id: string;
    title: string;
    description: string | null;
    version: number | null;
    questions: Array<{
      id: string;
      title: string;
      defaultPrompt: string | null;
      selfAssessmentPrompt: string | null;
      helpText: string | null;
      required: boolean | null;
      sortingIndex: number | null;
      inputType?: string | null;
      answerOptions: Array<{
        id: string;
        anchor: string | null;
        value: string;
        sortingIndex: number | null;
        helpText: string | null;
      }>;
    }>;
  };
};

export async function fetchInstrumentDetail(
  instrumentId: string
): Promise<GetInstrumentResponse> {
  return (await API.get("simpl-api", `instrument/${instrumentId}`, {
    queryStringParameters: {}
  })) as GetInstrumentResponse;
}

type AssessmentsListResponse = {
  assessments: Array<{
    id: string;
    title: string;
    description: string | null;
    instrumentId: string | null;
    sponsorId: string | null;
    requireTraineeSelfAssessment: boolean | null;
    creationDurationHours: number | null;
    expirationDurationHours: number | null;
  }>;
};

export async function searchAssessmentsByInstrumentAndSponsor(
  instrumentId: string,
  sponsorId: string,
  titleContains?: string
): Promise<AssessmentsListResponse> {
  const filter: Record<string, unknown> = {
    instrumentId: { eq: instrumentId },
    sponsorId: { eq: sponsorId }
  };
  if (titleContains && titleContains.trim()) {
    filter.title = { contains: titleContains.trim() };
  }
  return (await API.get("simpl-api", "assessments", {
    queryStringParameters: {
      limit: 100,
      filter: JSON.stringify(filter)
    }
  })) as AssessmentsListResponse;
}

type ActivitiesByAssessmentResponse = {
  activities: Array<{
    id: string;
    title: string;
    description: string | null;
    assessmentId: string | null;
    sponsorId: string | null;
  }>;
};

export async function fetchActivitiesByAssessment(
  assessmentId: string,
  options?: { title?: string; limit?: number }
): Promise<ActivitiesByAssessmentResponse> {
  const queryStringParameters: Record<string, string | number> = { assessmentId };
  if (options?.title && options.title.trim()) {
    queryStringParameters.title = options.title.trim();
  }
  if (typeof options?.limit === "number") {
    queryStringParameters.limit = options.limit;
  }
  return (await API.get("simpl-api", "activities-by-assessment", {
    queryStringParameters
  })) as ActivitiesByAssessmentResponse;
}
